/**
 * Songs library (F1): all songs synchronized by account `songs`
 * grants (a musician sees ONLY their groups' library).
 * Creation lives in group (song belongs to music group).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { getGroupsQuery, getSongsQuery, queryKeys } from "@agape/shared";
import type { ChurchGroupRecord, SongRecord } from "@agape/powersync";
import { Music } from "@fleet/icons";

import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { TextField } from "@fleet/ui/text-field";
import { useDomainQuery } from "../use-domain";

export function SongsPage() {
  const { t } = useI18n();
  const songs = useDomainQuery<SongRecord>(
    (uid) => queryKeys.domain.songs(uid),
    (db) => getSongsQuery(db),
  );
  const groups = useDomainQuery<ChurchGroupRecord>(
    (uid) => queryKeys.domain.groups(uid),
    (db) => getGroupsQuery(db),
  );
  const groupName = (id: string | null) => (groups.data ?? []).find((g) => g.id === id)?.name ?? "";

  const [search, setSearch] = createSignal("");
  const filtered = createMemo(() => {
    const q = search().trim().toLowerCase();
    const rows = songs.data ?? [];
    if (!q) {
      return rows;
    }
    return rows.filter((s) =>
      [s.title, s.author, s.tags].some((v) => v?.toLowerCase().includes(q)),
    );
  });

  return (
    <div class="space-y-4">
      <PageSubHeader title={t(msg`Songs`)} />
      <TextField
        value={search()}
        onInput={(e) => setSearch(e.currentTarget.value)}
        placeholder={t(msg`Search by title, author or tag…`)}
        class="max-w-xs"
        data-testid="songs-search"
      />
      <Show
        when={filtered().length > 0}
        fallback={
          <p data-testid="songs-empty" class="text-muted-foreground">
            {t(msg`No songs synced to this account. Songs live in your music groups.`)}
          </p>
        }
      >
        <div class="space-y-2" data-testid="songs-list">
          <For each={filtered()}>
            {(s) => (
              <Link to="/songs/$songId" params={{ songId: s.id }} class="block">
                <Card
                  data-testid="song-item"
                  data-song-id={s.id}
                  class="transition-colors hover:border-primary/40"
                >
                  <CardContent class="flex items-center gap-3 py-3">
                    <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                      <Music size={16} />
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="truncate font-medium">{s.title}</p>
                      <p class="truncate text-sm text-muted-foreground">
                        {[s.author, groupName(s.group_id)].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <p class="shrink-0 text-xs text-muted-foreground">
                      {[s.song_key, s.tempo ? `${s.tempo} bpm` : null].filter(Boolean).join(" · ")}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
